import { useState } from 'react';
import { Badge, Button, Empty, List, message, Modal, Popconfirm, Space, Spin } from 'antd';
import { DeleteOutlined, EditOutlined, TeamOutlined, PlusOutlined } from '@ant-design/icons';
import { TextService } from '../../Services/TextService';
import { CustomListType } from '../../Types/CustomListType';
import { WebApiCustomListsDtoCustomListItemDto, WebApiMemberDtoCustomListConnectionDto, WebApiMemberDtoMemberDto, WebApiModelsCustomListField } from '../../Repository/eventbookyapi';
import { CustomListService } from '../../Services/CustomListService';
import { CustomListFieldService } from '../../Services/CustomLifFieldService';
import CustomListAddModal from '../../Pages/CustomList/CustomListAddModal';
import CustomListEditModal from '../../Pages/CustomList/CustomListEditModal';
import { CustomListItemService } from '../../Services/CustomListItemService';
import { MemberService } from '../../Services/MemberService';

type AddFamilyMemberProps = {
    user: any;
    member: WebApiMemberDtoMemberDto;
    loadMembers?: () => void;
};

const AddFamilyMember = ({ user, member, loadMembers }: AddFamilyMemberProps) => {
    const GetText = TextService.GetText;

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);

    const [familyConnections, setFamilyConnections] = useState<WebApiMemberDtoCustomListConnectionDto[]>([]);
    const [familyCount, setFamilyCount] = useState(
        (member.customListConnections || []).filter(c => c.customListType === CustomListType.FamilyMember).length
    );

    const [familyCustomListId, setFamilyCustomListId] = useState<number | null>(null);
    const [customFieldsForModal, setCustomFieldsForModal] = useState<WebApiModelsCustomListField[]>([]);

    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editItem, setEditItem] = useState<WebApiCustomListsDtoCustomListItemDto | null>(null);

    const loadFamilyData = async () => {
        setLoading(true);
        try {
            let latestMember = member;
            try {
                const memberResult = await MemberService.GetAllMembers();
                const found = (memberResult.members || []).find(m => m.memberId === member.memberId);
                if (found) {
                    latestMember = found;
                }
            } catch {
                // Fall back to passed member prop
            }

            const connections = (latestMember.customListConnections || []).filter(
                c => c.customListType === CustomListType.FamilyMember
            );
            setFamilyConnections(connections);
            setFamilyCount(connections.length);

            let listId = connections[0]?.customListId ?? null;
            if (!listId) {
                const lists = await CustomListService.GetCustomLists();
                const familyList = lists.find(list => list.listType === CustomListType.FamilyMember);
                listId = familyList?.id ?? null;
            }

            setFamilyCustomListId(listId);

            if (listId) {
                const fields = await CustomListFieldService.GetCCustomListFields(listId) as WebApiModelsCustomListField[];
                setCustomFieldsForModal(fields);
            } else {
                setCustomFieldsForModal([]);
            }
        } catch {
            message.error(GetText('CustomList_FailedToLoad') || 'Could not load list data.');
        }
        setLoading(false);
    };

    const openModal = async () => {
        setOpen(true);
        await loadFamilyData();
    };

    const closeModal = () => {
        setOpen(false);
        setAddModalOpen(false);
        setEditModalOpen(false);
        setEditItem(null);
    };

    const handleAdd = async () => {
        if (!familyCustomListId) {
            message.warning(GetText('AddFamilyMember_NotActivated') || 'Family member list is not activated.');
            return;
        }
        setAddModalOpen(true);
    };

    const handleEdit = (connection: WebApiMemberDtoCustomListConnectionDto) => {
        if (!connection.customListItemId) return;
        setEditItem(connection.item || { id: connection.customListItemId });
        setEditModalOpen(true);
    };

    const handleDelete = async (connection: WebApiMemberDtoCustomListConnectionDto) => {
        if (!connection.customListItemId) return;

        setModalLoading(true);
        try {
            await CustomListItemService.DeleteCustomListItem(connection.customListItemId);
            message.success(GetText('CustomList_ItemDeleted') || 'Deleted!');
            await loadFamilyData();
            loadMembers && loadMembers();
        } catch {
            message.error(GetText('CustomList_FailedToDelete') || 'Could not delete the item.');
        }
        setModalLoading(false);
    };

    const handleModalSuccess = async () => {
        setAddModalOpen(false);
        setEditModalOpen(false);
        await loadFamilyData();
        loadMembers && loadMembers();
    };

    const getConnectionName = (connection: WebApiMemberDtoCustomListConnectionDto) => {
        const firstFieldValue = connection.item?.fieldValues?.[0]?.fieldValue;
        if (firstFieldValue && firstFieldValue.trim().length > 0) {
            return firstFieldValue;
        }

        return `${GetText('CustomList_Item') || 'Item'} #${connection.customListItemId}`;
    };

    return (
        <>
            <Modal
                open={open}
                width={700}
                title={`${GetText('AddFamilyMember_ModalTitle') || 'Family members'} - (${member.uniqueId}) ${member.firstName} ${member.lastName}`}
                onCancel={closeModal}
                onOk={closeModal}
                okText={GetText('EditMember_ModalClose') || 'Close'}
                cancelButtonProps={{ style: { display: 'none' } }}
            >
                <Space direction='vertical' style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            type='primary'
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                            disabled={user?.hasOnlyReadRights}
                        >
                            {GetText('CustomList_AddItem') || 'Add Item'}
                        </Button>
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 16 }}>
                            <Spin />
                        </div>
                    ) : familyConnections.length === 0 ? (
                        <Empty description={GetText('AddFamilyMember_Empty') || 'No family members connected'} />
                    ) : (
                        <List
                            dataSource={familyConnections}
                            renderItem={(connection) => (
                                <List.Item
                                    actions={[
                                        <Button
                                            key='edit'
                                            type='link'
                                            icon={<EditOutlined />}
                                            disabled={user?.hasOnlyReadRights || modalLoading}
                                            onClick={() => handleEdit(connection)}
                                        >
                                            {GetText('Common_Edit') || 'Edit'}
                                        </Button>,
                                        <Popconfirm
                                            key='delete'
                                            title={GetText('Common_ConfirmDelete') || 'Are you sure you want to delete?'}
                                            onConfirm={() => handleDelete(connection)}
                                            okText={GetText('Common_Yes') || 'Yes'}
                                            cancelText={GetText('Common_No') || 'No'}
                                        >
                                            <Button
                                                type='link'
                                                danger
                                                icon={<DeleteOutlined />}
                                                disabled={user?.hasOnlyReadRights || modalLoading}
                                            >
                                                {GetText('Common_Delete') || 'Delete'}
                                            </Button>
                                        </Popconfirm>
                                    ]}
                                >
                                    {getConnectionName(connection)}
                                </List.Item>
                            )}
                        />
                    )}
                </Space>
            </Modal>

            <Badge color={'blue'} className={familyCount > 0 ? '' : 'not-payed'}>
                <TeamOutlined
                    style={{ fontSize: '20px', color: familyCount > 0 ? '#000' : '#bfbfbf' }}
                    onClick={openModal}
                />
            </Badge>

            <CustomListEditModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                onSuccess={handleModalSuccess}
                customFields={customFieldsForModal}
                item={editItem!}
                hasOnlyReadRights={user?.hasOnlyReadRights}
            />
            <CustomListAddModal
                open={addModalOpen}
                member={member}
                onClose={() => setAddModalOpen(false)}
                onSuccess={handleModalSuccess}
                customFields={customFieldsForModal}
                customListId={familyCustomListId || 1}
                hasOnlyReadRights={user?.hasOnlyReadRights}
            />
        </>
    );
};

export default AddFamilyMember;
